import { toCanvas } from "html-to-image";
import { jsPDF } from "jspdf";

const PAGE_WIDTH_MM = 210;
const PAGE_HEIGHT_MM = 297;
const MARGIN_MM = 10;
const CONTENT_WIDTH_MM = PAGE_WIDTH_MM - MARGIN_MM * 2;
const CONTENT_HEIGHT_MM = PAGE_HEIGHT_MM - MARGIN_MM * 2;
const JPEG_QUALITY = 0.8;
const CAPTURE_PIXEL_RATIO = 2;
// Blocks taller than this share of a page recurse into their children for break
// candidates; smaller blocks are kept together on one page. 0.5 keeps signature
// cards whole while letting long checklist cards fill pages instead of leaving
// half-empty pages behind.
const MAX_UNBREAKABLE_PAGE_SHARE = 0.5;

const parseSchemaRoot = (schema) => {
  try {
    const parsed = typeof schema === "string" ? JSON.parse(schema) : schema;
    return (parsed && parsed.form) || parsed || null;
  } catch (err) {
    return null;
  }
};

const collectSignatureKeys = (node, keys = []) => {
  if (!node || typeof node !== "object") return keys;
  // dataKey overrides key for data binding when present (see persisted-form.schema.json)
  if (node.type === "RsSignature" && (node.dataKey || node.key)) keys.push(node.dataKey || node.key);
  if (Array.isArray(node.children)) {
    node.children.forEach((child) => collectSignatureKeys(child, keys));
  }
  return keys;
};

const loadImage = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const isCanvasBlank = (canvas) => {
  try {
    const ctx = canvas.getContext("2d");
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] !== 0) return false;
    }
    return true;
  } catch (err) {
    return false;
  }
};

/**
 * The form-builder viewer applies stored data in an effect after children mount,
 * but the signature component only draws its value into the signature_pad canvas
 * in its own mount effect (deps exclude value), so stored signatures render as
 * blank canvases. Canvas DOM ids are generated (not field keys), so signature
 * keys collected from the schema in tree order are paired with signature
 * canvases in DOM order. Only blank canvases are drawn, so strokes the user
 * drew after mount are never overwritten. Safe to call repeatedly.
 */
export const drawStoredSignatures = async (rootEl, data, schema) => {
  if (!rootEl || !data) return;
  const keys = collectSignatureKeys(parseSchemaRoot(schema));
  if (!keys.length) return;

  let canvases = Array.from(rootEl.querySelectorAll('canvas[id^="RsSignature"]'));
  if (canvases.length !== keys.length) {
    canvases = Array.from(rootEl.querySelectorAll("canvas"));
  }

  await Promise.all(
    keys.map(async (key, index) => {
      const canvas = canvases[index];
      const value = data[key];
      if (!canvas || typeof value !== "string" || !value.startsWith("data:image")) return;
      if (!isCanvasBlank(canvas)) return;
      const img = await loadImage(value);
      if (!img) return;
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    })
  );
};

// Hide (not filter) the signature "Clear" buttons: removing nodes in the
// html-to-image clone shifts layout and a label-less signature component's
// wrapper has textContent === "Clear", so a text filter would drop the whole
// signature. display:none before measuring keeps pagination and capture in sync.
const hideClearButtons = (rootEl, cleanups) => {
  rootEl.querySelectorAll("button").forEach((button) => {
    if (button.textContent.trim() !== "Clear") return;
    const prevDisplay = button.style.display;
    button.style.display = "none";
    cleanups.push(() => {
      button.style.display = prevDisplay;
    });
  });
};

const expandTextareas = (rootEl, cleanups) => {
  rootEl.querySelectorAll("textarea").forEach((textarea) => {
    if (textarea.scrollHeight <= textarea.clientHeight) return;
    const prevHeight = textarea.style.height;
    const prevOverflow = textarea.style.overflow;
    textarea.style.height = `${textarea.scrollHeight}px`;
    textarea.style.overflow = "hidden";
    cleanups.push(() => {
      textarea.style.height = prevHeight;
      textarea.style.overflow = prevOverflow;
    });
  });
};

// html-to-image captures <canvas> content unreliably, so each canvas is swapped
// for an <img> snapshot for the duration of the capture.
const snapshotCanvases = async (rootEl, cleanups) => {
  const decodes = [];
  rootEl.querySelectorAll("canvas").forEach((canvas) => {
    let dataUrl;
    try {
      dataUrl = canvas.toDataURL("image/png");
    } catch (err) {
      return;
    }
    const rect = canvas.getBoundingClientRect();
    const img = document.createElement("img");
    img.src = dataUrl;
    img.style.cssText = canvas.style.cssText;
    img.style.width = `${rect.width}px`;
    img.style.height = `${rect.height}px`;
    canvas.parentNode.insertBefore(img, canvas);
    const prevDisplay = canvas.style.display;
    canvas.style.display = "none";
    cleanups.push(() => {
      canvas.style.display = prevDisplay;
      img.remove();
    });
    if (img.decode) decodes.push(img.decode().catch(() => {}));
  });
  await Promise.all(decodes);
};

// Extents must include absolutely-positioned descendants: MUI floating labels
// poke above their container's own rect.
const unionExtent = (el, rootTop) => {
  const rect = el.getBoundingClientRect();
  let top = rect.top;
  let bottom = rect.bottom;
  el.querySelectorAll("*").forEach((child) => {
    const childRect = child.getBoundingClientRect();
    if (!childRect.height && !childRect.width) return;
    if (childRect.top < top) top = childRect.top;
    if (childRect.bottom > bottom) bottom = childRect.bottom;
  });
  return { top: top - rootTop, bottom: bottom - rootTop };
};

const collectBlocks = (el, rootTop, maxHeight, blocks) => {
  Array.from(el.children).forEach((child) => {
    const extent = unionExtent(child, rootTop);
    const height = extent.bottom - extent.top;
    if (height <= 0) return;
    if (height <= maxHeight || !child.children.length) {
      blocks.push(extent);
    } else {
      collectBlocks(child, rootTop, maxHeight, blocks);
    }
  });
};

export const computePageSegments = (element) => {
  const rootRect = element.getBoundingClientRect();
  const pxPerMm = rootRect.width / CONTENT_WIDTH_MM;
  const pageHeightPx = CONTENT_HEIGHT_MM * pxPerMm;

  const blocks = [];
  collectBlocks(element, rootRect.top, pageHeightPx * MAX_UNBREAKABLE_PAGE_SHARE, blocks);
  blocks.sort((a, b) => a.top - b.top);

  // Trailing empty space below the last content block would otherwise emit blank pages.
  const contentBottom = blocks.reduce((max, block) => Math.max(max, block.bottom), 0);
  const totalHeight = contentBottom > 0 ? Math.min(rootRect.height, contentBottom + 8) : rootRect.height;

  const segments = [];
  let cursor = 0;
  while (totalHeight - cursor > pageHeightPx) {
    let cut = cursor + pageHeightPx;
    for (const block of blocks) {
      if (block.top >= cut) break;
      // A block would be sliced by this cut: move the cut up to the block's top
      // (unless the block starts at the page top, i.e. it is taller than a page).
      if (block.bottom > cut && block.top > cursor + 1) {
        cut = block.top;
      }
    }
    segments.push({ start: cursor, end: cut });
    cursor = cut;
  }
  segments.push({ start: cursor, end: totalHeight });
  return { segments, pxPerMm, blocks };
};

// html-to-image silently downscales any capture canvas whose width or height
// exceeds this (checkCanvasDimensions), which blurs every page of long forms.
const MAX_CANVAS_DIMENSION = 16384;

/**
 * Renders a form DOM element into a paginated A4 jsPDF document. Pages break at
 * element boundaries instead of fixed offsets, and each page embeds only its own
 * slice as a compressed JPEG so the file stays small enough to email.
 *
 * Long forms are captured in chunks of consecutive page segments so each chunk's
 * canvas stays under MAX_CANVAS_DIMENSION at full pixel ratio — a single
 * full-height capture would be downscaled and come out blurry. Each chunk crops
 * the clone to its own region via a translateY transform.
 */
export const generateFormPdf = async (element) => {
  const cleanups = [];
  try {
    hideClearButtons(element, cleanups);
    expandTextareas(element, cleanups);
    await snapshotCanvases(element, cleanups);
    const { segments, pxPerMm } = computePageSegments(element);
    const cssWidth = element.getBoundingClientRect().width;

    const maxChunkCssHeight = Math.floor(MAX_CANVAS_DIMENSION / CAPTURE_PIXEL_RATIO);
    const chunks = [];
    let chunk = [];
    for (const segment of segments) {
      if (chunk.length && segment.end - chunk[0].start > maxChunkCssHeight) {
        chunks.push(chunk);
        chunk = [];
      }
      chunk.push(segment);
    }
    if (chunk.length) chunks.push(chunk);

    const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4", compress: true });
    let pageIndex = 0;

    for (const group of chunks) {
      const chunkStart = group[0].start;
      const chunkHeight = group[group.length - 1].end - chunkStart;
      const canvas = await toCanvas(element, {
        pixelRatio: CAPTURE_PIXEL_RATIO,
        backgroundColor: "#ffffff",
        width: cssWidth,
        height: chunkHeight,
        style: { transform: `translateY(${-chunkStart}px)` },
      });
      const ratio = canvas.height / chunkHeight;

      for (const segment of group) {
        const segmentHeightPx = segment.end - segment.start;
        if (segmentHeightPx <= 0) continue;

        const slice = document.createElement("canvas");
        slice.width = canvas.width;
        slice.height = Math.max(1, Math.round(segmentHeightPx * ratio));
        const ctx = slice.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, slice.width, slice.height);
        ctx.drawImage(
          canvas,
          0,
          Math.round((segment.start - chunkStart) * ratio),
          canvas.width,
          slice.height,
          0,
          0,
          slice.width,
          slice.height
        );

        if (pageIndex > 0) pdf.addPage();
        pageIndex += 1;
        pdf.addImage(
          slice.toDataURL("image/jpeg", JPEG_QUALITY),
          "JPEG",
          MARGIN_MM,
          MARGIN_MM,
          CONTENT_WIDTH_MM,
          segmentHeightPx / pxPerMm
        );
      }
    }

    return pdf;
  } finally {
    cleanups.reverse().forEach((cleanup) => cleanup());
  }
};
